import { useCallback, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  blocksAutomaticStart,
  type OnboardingTourId,
  type OnboardingTourProgress,
  type OnboardingTourProgressResponse,
  type OnboardingTourStatus,
} from "@playbookai/types";
import { loadTourDefinitions, type TourDefinition } from "@playbookai/ui";
import { useAuthStore } from "../auth/auth-store.js";
import { listTourProgress, putTourProgress } from "../api/onboarding-api.js";
import rawDefinitions from "./tour-definitions.json";

/**
 * Validated ONCE per module load, not per render: the definitions are static
 * repository code, so re-parsing them on every render would be pure cost.
 *
 * A malformed definition disables its own tour and nothing else — the product
 * page it would have run on is unaffected, which is the whole reason the loader
 * returns a partial set rather than throwing.
 */
const DEFINITIONS = loadTourDefinitions(rawDefinitions as readonly unknown[]);

export function tourDefinition(
  id: OnboardingTourId,
): TourDefinition | undefined {
  return DEFINITIONS.tours.get(id);
}

/** Definitions rejected at load, for a boot-time warning and for specs. */
export function rejectedTourDefinitions(): readonly string[] {
  return DEFINITIONS.rejected;
}

export interface OnboardingTours {
  /** True once the progress read has an answer. Nothing starts before this. */
  readonly ready: boolean;
  /**
   * May this tour start on its own right now?
   *
   * FALSE while the read is loading or has failed, which is the load-bearing
   * part: a failed read is not evidence of "no record", and treating it as one
   * would start a completed tour again on every retry.
   */
  readonly eligible: (id: OnboardingTourId) => boolean;
  readonly progressFor: (
    id: OnboardingTourId,
  ) => OnboardingTourProgress | undefined;
  /** The tour currently on screen, or null. Exactly one runs at a time. */
  readonly active: OnboardingTourId | null;
  /** Start (or resume) a tour, if nothing else is running. */
  readonly request: (id: OnboardingTourId) => void;
  /** Replay: runs the tour WITHOUT clearing its stored completion. */
  readonly replay: (id: OnboardingTourId) => void;
  readonly record: (
    id: OnboardingTourId,
    status: OnboardingTourStatus,
    lastStepId: string | null,
  ) => void;
  readonly dismissActive: () => void;
}

export function useOnboardingTours(visitKey = ""): OnboardingTours {
  const claims = useAuthStore((state) => state.claims);
  const authed = claims?.tenantId != null && claims?.userId != null;
  const cache = useQueryClient();
  const key = useMemo(
    () => ["onboarding-tours", claims?.tenantId, claims?.userId, visitKey],
    [claims?.tenantId, claims?.userId, visitKey],
  );
  const query = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => listTourProgress(signal),
    enabled: authed,
    staleTime: 0,
    refetchOnWindowFocus: false,
    // A failed read must not be retried into a start: `eligible` already
    // refuses while `isSuccess` is false, so one bounded retry is generous.
    retry: 1,
  });

  const [active, setActive] = useState<OnboardingTourId | null>(null);
  /**
   * A replay is running. Held apart from `active` because it changes ONE
   * thing: the terminal write at the end is suppressed, so replaying an
   * already-completed tour and then abandoning it cannot demote the record to
   * `in_progress` — which would make the tour start again by itself.
   */
  const [replaying, setReplaying] = useState(false);
  // Suppress automatic starts synchronously on Finish/Skip, before the save
  // returns. This set belongs to this tenant/user and resets on identity change.
  // Failed saves can be retried on a later visit without reopening the tour
  // underneath someone who has just dismissed it.
  const locallyDismissed = useMemo(() => new Set<OnboardingTourId>(), [key]);

  const byId = useMemo(() => {
    const map = new Map<string, OnboardingTourProgress>();
    for (const entry of query.data?.progress ?? [])
      map.set(entry.tourId, entry);
    return map;
  }, [query.data]);

  const activeGeneration = useRef(0);

  const mutation = useMutation({
    mutationFn: putTourProgress,
    onSuccess: (saved, request) => {
      // The server may have REFUSED a late `in_progress` and answered the
      // winning record. Taking its answer rather than the request keeps the
      // cache honest without surfacing a conflict nobody caused.
      cache.setQueryData<OnboardingTourProgressResponse>(key, (current) => {
        if (
          (request.generation ?? 0) !==
          (current?.generations?.[saved.tourId] ?? 0)
        )
          return current;
        const existing = current?.progress.find(
          (entry) => entry.tourId === saved.tourId,
        );
        // An older step write can respond after the terminal write. Never
        // regress a completion/skip to in_progress because of response order.
        if (blocksAutomaticStart(existing) && !blocksAutomaticStart(saved))
          return current;
        const rest = (current?.progress ?? []).filter(
          (entry) => entry.tourId !== saved.tourId,
        );
        return { ...current, progress: [...rest, saved] };
      });
    },
    onError: () => {
      // A failed write leaves the server as the truth. Re-reading is what stops
      // a tour looking finished locally while the server still offers it.
      void cache.invalidateQueries({ queryKey: key });
    },
  });

  const record = useCallback(
    (
      id: OnboardingTourId,
      status: OnboardingTourStatus,
      lastStepId: string | null,
    ): void => {
      const definition = DEFINITIONS.tours.get(id);
      if (!definition) return;
      /* A REPLAY records nothing at all, and both halves matter. It must not
         write `in_progress`, because abandoning a replay would then demote a
         completed record and the tour would start by itself again; and it must
         not write a terminal status either, because that would move
         `completedAt` to a moment that was not the first time. Replay is a
         re-viewing, not a re-doing. */
      if (replaying) return;
      if (status === "completed" || status === "skipped")
        locallyDismissed.add(id);
      mutation.mutate({
        generation: activeGeneration.current,
        tourId: id,
        tourVersion: definition.version,
        status,
        lastStepId,
      });
    },
    [mutation, replaying, locallyDismissed],
  );

  const eligible = useCallback(
    (id: OnboardingTourId): boolean => {
      if (locallyDismissed.has(id)) return false;
      if (!query.isSuccess || query.isFetching) return false;
      if (!DEFINITIONS.tours.has(id)) return false;
      return !blocksAutomaticStart(byId.get(id));
    },
    [byId, query.isSuccess, query.isFetching, locallyDismissed],
  );

  const request = useCallback(
    (id: OnboardingTourId): void => {
      // ONE tour at a time. A second eligible trigger does not queue here on
      // purpose — its own trigger condition is re-evaluated on the next render
      // after the first tour ends, which is a stable UI state by definition.
      if (active !== null) return;
      activeGeneration.current = query.data?.generations?.[id] ?? 0;
      setReplaying(false);
      setActive(id);
    },
    [active, query.data],
  );

  const replay = useCallback(
    (id: OnboardingTourId): void => {
      if (active !== null) return;
      setReplaying(true);
      setActive(id);
    },
    [active],
  );

  const dismissActive = useCallback((): void => {
    setActive(null);
    setReplaying(false);
  }, []);

  return {
    ready: query.isSuccess && !query.isFetching,
    eligible,
    progressFor: (id) => byId.get(id),
    active,
    request,
    replay,
    record,
    dismissActive,
  };
}
